import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

type GuestConfig = {
  habilitado: boolean;
  max_invitaciones_periodo: number;
  periodo_dias: number;
  antiguedad_minima_dias: number;
  vigencia_horas: number;
  requiere_acompanamiento: boolean;
  una_activa_por_socio: boolean;
  instrucciones: string;
};

const defaults: GuestConfig = {
  habilitado: false,
  max_invitaciones_periodo: 1,
  periodo_dias: 30,
  antiguedad_minima_dias: 0,
  vigencia_horas: 24,
  requiere_acompanamiento: true,
  una_activa_por_socio: true,
  instrucciones: '',
};

export default function GuestSettings({ gimnasioId, onClose }: { gimnasioId: string | null; onClose: () => void }) {
  const [form, setForm] = useState<GuestConfig>(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!gimnasioId) { setError('No se pudo determinar el gimnasio activo.'); setLoading(false); return; }
    const { data, error: loadError } = await supabase.from('invitados_configuracion').select('*').eq('gimnasio_id', gimnasioId).maybeSingle();
    if (loadError) setError(loadError.message);
    if (data) setForm({
      habilitado: data.habilitado,
      max_invitaciones_periodo: data.max_invitaciones_periodo,
      periodo_dias: data.periodo_dias,
      antiguedad_minima_dias: data.antiguedad_minima_dias,
      vigencia_horas: data.vigencia_horas,
      requiere_acompanamiento: data.requiere_acompanamiento,
      una_activa_por_socio: data.una_activa_por_socio,
      instrucciones: data.instrucciones || '',
    });
    setLoading(false);
  }, [gimnasioId]);

  useEffect(() => { void load(); }, [load]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault(); if (!gimnasioId) return;
    setSaving(true); setError(null);
    const { error: saveError } = await supabase.from('invitados_configuracion').upsert({
      gimnasio_id: gimnasioId, ...form, instrucciones: form.instrucciones.trim() || null,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'gimnasio_id' });
    if (saveError) setError(saveError.message); else onClose();
    setSaving(false);
  };

  const number = (key: keyof GuestConfig, value: string) => setForm((current) => ({ ...current, [key]: Number(value) }));

  return <div className="text-left flex flex-col max-h-[calc(100vh-2rem)] overflow-hidden rounded-xl">
    <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4 shrink-0">
      <div><h3 className="text-lg font-semibold text-gray-900">Invitados</h3><p className="text-sm text-gray-500 mt-1">Define quién puede invitar, cuántas veces y bajo qué condiciones</p></div>
      <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Cerrar"><i className="ri-close-line text-xl text-gray-500" /></button>
    </div>
    <form onSubmit={save} className="flex flex-col min-h-0">
      <div className="px-6 py-5 overflow-y-auto space-y-5 min-h-0">
      {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
      {loading ? <div className="py-16 text-center text-gray-500"><i className="ri-loader-4-line animate-spin text-2xl" /></div> : <>
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900"><strong>Importante:</strong> enviar una solicitud no habilita el acceso. Un administrador debe aprobarla desde la bandeja de invitados.</div>
        <label className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-gray-50">
          <div><p className="font-semibold text-gray-900">Habilitar invitaciones</p><p className="text-xs text-gray-500 mt-1">Muestra “Invitar a un amigo” y permite enviar solicitudes.</p></div>
          <input type="checkbox" className="w-5 h-5" checked={form.habilitado} onChange={(e) => setForm({ ...form, habilitado: e.target.checked })} />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Invitaciones por período"><input className="input" type="number" min="0" max="100" required value={form.max_invitaciones_periodo} onChange={(e) => number('max_invitaciones_periodo', e.target.value)} /></Field>
          <Field label="Duración del período (días)"><input className="input" type="number" min="1" max="366" required value={form.periodo_dias} onChange={(e) => number('periodo_dias', e.target.value)} /></Field>
          <Field label="Antigüedad mínima del socio (días)"><input className="input" type="number" min="0" max="3650" required value={form.antiguedad_minima_dias} onChange={(e) => number('antiguedad_minima_dias', e.target.value)} /></Field>
          <Field label="Vigencia después de aprobar (horas)"><input className="input" type="number" min="1" max="168" required value={form.vigencia_horas} onChange={(e) => number('vigencia_horas', e.target.value)} /></Field>
        </div>
        <div className="space-y-3">
          <Check label="Debe ingresar acompañado por el socio" checked={form.requiere_acompanamiento} onChange={(value) => setForm({ ...form, requiere_acompanamiento: value })} />
          <Check label="Permitir solo una solicitud pendiente o aprobada por socio" checked={form.una_activa_por_socio} onChange={(value) => setForm({ ...form, una_activa_por_socio: value })} />
        </div>
        <Field label="Instrucciones para el socio"><textarea className="input min-h-24" value={form.instrucciones} onChange={(e) => setForm({ ...form, instrucciones: e.target.value })} placeholder="Ej: Presentarse juntos en recepción con identificación." /></Field>
      </>}
      </div>
      {!loading && <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 shrink-0"><button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 bg-white rounded-lg">Cancelar</button><button disabled={saving} className="px-5 py-2 bg-teal-700 text-white rounded-lg disabled:opacity-50">{saving ? 'Guardando…' : 'Guardar configuración'}</button></div>}
    </form>
  </div>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) { return <label className="block"><span className="block text-sm font-medium text-gray-700 mb-1.5">{label}</span>{children}</label>; }
function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) { return <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200"><input type="checkbox" className="w-4 h-4" checked={checked} onChange={(e) => onChange(e.target.checked)} /><span className="text-sm text-gray-700">{label}</span></label>; }
